import {useCallback, useMemo} from 'react'
import {IntlShape} from 'react-intl'

import {ChatPageContext} from '../../pageContext/pageContextTypes'
import {
  AttendanceChatMessage,
  AttendanceChatSendExtras,
  ConversationHistoryItem,
} from '../attendance/attendanceWizardTypes'
import {useAttendanceChat} from '../attendance/useAttendanceChat'
import {AssistantClarification} from './assistantResponseContract'
import {ChatSurface, chatEndpointFor, isCapabilityAllowedOnSurface} from './chatSurface'
import {useChatViewer} from './useChatViewer'

type SetMessages = React.Dispatch<React.SetStateAction<AttendanceChatMessage[]>>
type SetHistory = React.Dispatch<React.SetStateAction<ConversationHistoryItem[]>>

type UseAssistantChatParams = {
  intl: IntlShape
  messages: AttendanceChatMessage[]
  setMessages: SetMessages
  conversationHistory: ConversationHistoryItem[]
  setConversationHistory: SetHistory
  /** The surface this conversation belongs to. `admission_admin` / `admission_applicant` change
   *  what is sent; `admission_applicant` also changes where (`admission/chat`). */
  surface?: ChatSurface
  /** Read when a message is sent, so the page and screen it describes are the ones the message
   *  was sent from (`options.pageContext`). Left unset on the general surface so its payload
   *  stays exactly what it was — except on the selective AI domains, where the help bot is told
   *  the screen (`surface: 'ai_help'`). */
  getPageContext?: () => ChatPageContext | null
  welcomeMessageId?: string
}

/**
 * One conversation, one send path.
 *
 * The client used to pick a backend board per message by regex and hand each board only the
 * history it had produced. A follow-up the regex could not place ("I need overall attendence",
 * "give me the overall summary") went to the knowledge base with the attendance thread stripped
 * out, so the backend answered the only question it could see — from the manual. The backend
 * has always routed from the whole history and ignores any board the client names, so the
 * guess steered nothing except which half of the history got deleted.
 *
 * Now every turn carries the full history and the backend's `capability` on each answer, and a
 * message's `source` is read off the response rather than decided before it was sent.
 *
 * The page keeps one instance per surface (general, admission), each with its own state, so a
 * reply always lands in the conversation that asked for it.
 */
export function useAssistantChat({
  intl,
  messages,
  setMessages,
  conversationHistory,
  setConversationHistory,
  surface = 'general',
  getPageContext,
  welcomeMessageId = 'CHATBOT.ASSISTANT.WELCOME',
}: UseAssistantChatParams) {
  const t = useCallback((id: string) => intl.formatMessage({id}), [intl])
  const viewer = useChatViewer()

  const {
    isTyping,
    resetAssistiveState,
    handleSendMessage: send,
  } = useAttendanceChat({
    intl,
    setMessages,
    conversationHistory,
    setConversationHistory,
    // A signed-in applicant's turns go to `admission/chat` with their token; anyone else on the
    // applicant surface (signed out, or a non-applicant student/parent) goes to
    // `admission/public-chat` without one.
    endpoint: chatEndpointFor(surface, viewer),
  })

  /**
   * The clarification the assistant last asked. Read from the conversation rather than kept in
   * a ref so it survives whatever the reply turns out to be, and cleared as soon as a later bot
   * answer lands — a clarification is only ever echoed onto the turn that replies to it.
   */
  const pendingClarification = useMemo<AssistantClarification | null>(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i]
      if (message.sender !== 'bot' || message.id === 'welcome') continue
      return message.clarification ?? null
    }
    return null
  }, [messages])

  const handleSendMessage = useCallback(
    (text: string, sendExtras?: AttendanceChatSendExtras) => {
      const trimmed = text?.trim()
      if (!trimmed || isTyping) return

      let extras: AttendanceChatSendExtras | undefined =
        pendingClarification && !sendExtras?.pendingClarification
          ? {...(sendExtras ?? {}), pendingClarification}
          : sendExtras

      // A chip left over from another surface can still name attendance or the directory. The
      // backend ignores it on a scoped turn; not sending it keeps the request honest.
      if (extras?.capabilityId && !isCapabilityAllowedOnSurface(extras.capabilityId, surface)) {
        extras = {...extras, capabilityId: undefined}
      }
      const pageContext = getPageContext?.() ?? null
      if (pageContext) {
        extras = {...(extras ?? {}), pageContext}
      }

      send(trimmed, extras)
    },
    [getPageContext, isTyping, pendingClarification, send, surface]
  )

  const initializeWelcome = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        text: t(welcomeMessageId),
        sender: 'bot',
        timestamp: new Date(),
      },
    ])
  }, [setMessages, t, welcomeMessageId])

  return {
    isTyping,
    initializeWelcome,
    resetAssistiveState,
    handleSendMessage,
  }
}
